
import { Component, Watch } from 'vue-property-decorator';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import AnnouncementRepository from '@/api/repositories/AnnouncementRepository';
import CompanyRepository from '@/api/repositories/CompanyRepository';
import { Audience } from '@/enum/Audience.enum';
import { validationMixin } from 'vuelidate';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { mixins } from 'vue-class-component';
import ErrorMessageHandlerMixin from '@/misc/ErrorMessageHandler.mixin';

const announcementRepository: AnnouncementRepository = RepositoryFactory.get('announcement');
const companyRepository: CompanyRepository = RepositoryFactory.get('company');

/**
 * While a row is SENDING the table re-reads itself this often, so the
 * outcome of a detached send shows up on its own. No row in flight = no
 * polling at all. Same mechanics as LiveCounts (pause when hidden,
 * cleared on destroy, stale responses discarded).
 */
const POLL_INTERVAL_MS = 3000;

interface AudienceSelect {
    title: string,
    value: Audience
}

@Component({
    mixins: [validationMixin],
    validations: {
        title: { required },
        body: { required },
        audience: { required },
        company: {
            required: requiredIf(function (this: any) {
                return this.audience === Audience.COMPANY_DRIVERS;
            })
        }
    }
})
export default class NotificationView extends mixins(ErrorMessageHandlerMixin) {
    // Composer state
    private title: string = '';
    private body: string = '';
    private audience: Audience | null = null;
    private company: string | null = null;
    private activeFrom: string = '';
    private activeUntil: string = '';
    private isCounting: boolean = false;
    private isSending: boolean = false;
    private isSavingDraft: boolean = false;

    // Confirmation dialog state. `confirmTargetId` distinguishes the two
    // paths through the dialog: null = composer send, id = publishing an
    // existing draft from the history table.
    private confirmVisible: boolean = false;
    private confirmCount: number = 0;
    private confirmAudienceLabel: string = '';
    private confirmScheduledFor: string | null = null;
    private confirmTargetId: string | null = null;

    // History table state
    private historyItems: any[] = [];
    private historyTotal: number = 0;
    private historyLoading: boolean = false;
    private historyOptions: any = { page: 1, itemsPerPage: 10 };
    private statusFilter: string | null = null;
    private pollTimer: number | null = null;
    /** Identifies the most recent history request; a stale response is discarded. */
    private loadToken: number = 0;
    /** Bound once so removeEventListener gets the reference addEventListener registered. */
    private onVisibilityChange = () => {
        if (document.visibilityState === 'visible' && this.pollTimer !== null) {
            this.loadHistory(true);
        }
    };

    // Company picker
    private companies: any[] = [];
    private companiesLoading: boolean = false;

    private audiences: AudienceSelect[] = [
        { title: this.$t('NOTIFICATIONS.AUDIENCES.DRIVER').toString(), value: Audience.DRIVER },
        { title: this.$t('NOTIFICATIONS.AUDIENCES.USER').toString(), value: Audience.USER },
        { title: this.$t('NOTIFICATIONS.AUDIENCES.COMPANY_DRIVERS').toString(), value: Audience.COMPANY_DRIVERS }
    ];

    private statusFilterItems = ['DRAFT', 'SCHEDULED', 'PUBLISHED', 'ARCHIVED'].map((status) => ({
        title: this.$t(`NOTIFICATIONS.STATUS.${status}`).toString(),
        value: status
    }));

    private historyHeaders = [
        { text: this.$t('NOTIFICATIONS.HISTORY.HEADERS.CREATED').toString(), value: 'createdAt' },
        { text: this.$t('NOTIFICATIONS.HISTORY.HEADERS.TITLE').toString(), value: 'title' },
        { text: this.$t('NOTIFICATIONS.HISTORY.HEADERS.AUDIENCE').toString(), value: 'audience' },
        { text: this.$t('NOTIFICATIONS.HISTORY.HEADERS.STATUS').toString(), value: 'status' },
        { text: this.$t('NOTIFICATIONS.HISTORY.HEADERS.SENT_AT').toString(), value: 'sentAt' },
        { text: this.$t('NOTIFICATIONS.HISTORY.HEADERS.DELIVERED').toString(), value: 'delivered' },
        { text: this.$t('NOTIFICATIONS.HISTORY.HEADERS.ACTIONS').toString(), value: 'actions', sortable: false }
    ];

    private get isCompanyScoped(): boolean {
        return this.audience === Audience.COMPANY_DRIVERS;
    }

    private get isScheduling(): boolean {
        return !!this.activeFrom && new Date(this.activeFrom).getTime() > Date.now();
    }

    public async created(): Promise<void> {
        this.loadCompanies();
        await this.loadHistory();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    public beforeDestroy(): void {
        this.stopPolling();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    @Watch('historyOptions', { deep: true })
    private onHistoryOptionsChanged(): void {
        this.loadHistory();
    }

    private onStatusFilterChanged(): void {
        this.historyOptions = { ...this.historyOptions, page: 1 };
    }

    private async loadCompanies(): Promise<void> {
        try {
            this.companiesLoading = true;
            const response = await companyRepository.list(false);
            this.companies = response.data || [];
        } catch (e) {
            this.$notifyErrorSimplified('NOTIFICATIONS.COMPANIES_LOAD_ERROR');
        } finally {
            this.companiesLoading = false;
        }
    }

    /**
     * @param auto true for poll/visibility-triggered refreshes: those skip
     * while the tab is hidden, do not flash the table's loading bar, and fail
     * silently rather than toasting every few seconds if the backend is away.
     */
    private async loadHistory(auto: boolean = false): Promise<void> {
        if (auto && document.visibilityState === 'hidden') {
            return;
        }
        const token = ++this.loadToken;
        try {
            if (!auto) {
                this.historyLoading = true;
            }
            const response = await announcementRepository.list({
                page: this.historyOptions.page,
                pageSize: this.historyOptions.itemsPerPage,
                status: this.statusFilter || undefined
            });
            if (token !== this.loadToken) return;
            this.historyItems = response.data.items || [];
            this.historyTotal = response.data.total || 0;
            this.syncPolling();
        } catch (e) {
            if (!auto) {
                this.$notifyErrorSimplified('NOTIFICATIONS.LOAD_ERROR');
            }
        } finally {
            if (!auto) {
                this.historyLoading = false;
            }
        }
    }

    /** Poll only while a visible row is still being sent; stop as soon as none is. */
    private syncPolling(): void {
        const inFlight = this.historyItems.some((item: any) => this.deliveryState(item) === 'SENDING');
        if (inFlight && this.pollTimer === null) {
            this.pollTimer = window.setInterval(() => this.loadHistory(true), POLL_INTERVAL_MS);
        } else if (!inFlight) {
            this.stopPolling();
        }
    }

    private stopPolling(): void {
        if (this.pollTimer !== null) {
            window.clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    /**
     * What the Delivered column shows. Rows written since the detached send
     * carry pushStatus; older rows (null) are derived from the counts, exactly
     * as before.
     */
    private deliveryState(item: any): string | null {
        if (item.pushStatus) {
            return item.pushStatus;
        }
        if (item.pushError) {
            return 'FAILED';
        }
        if (item.pushSuccessCount !== null && item.pushSuccessCount !== undefined) {
            return 'SENT';
        }
        return null;
    }

    /**
     * Composer "Send"/"Schedule": validate, fetch the REAL recipient count,
     * and only then show the confirmation dialog. Nothing is created or
     * sent unless the admin confirms.
     */
    private async openSendConfirmation(): Promise<void> {
        this.$v.$touch();
        if (this.$v.$invalid) return;

        try {
            this.isCounting = true;
            const response = await announcementRepository.recipientCount(
                this.audience!, this.isCompanyScoped ? this.company! : undefined);
            this.confirmCount = response.data.count;
            this.confirmAudienceLabel = this.audienceSummary(this.audience!, this.company);
            this.confirmScheduledFor = this.isScheduling ? this.formatDate(this.activeFrom) : null;
            this.confirmTargetId = null;
            this.confirmVisible = true;
        } catch (e) {
            this.$notifyErrorSimplified('NOTIFICATIONS.COUNT_ERROR');
        } finally {
            this.isCounting = false;
        }
    }

    /** History-table publish on a DRAFT: same dialog, same real count. */
    private async openRowConfirmation(item: any): Promise<void> {
        try {
            this.isCounting = true;
            const companyId = item.company ? (item.company.id || item.company._id || item.company) : undefined;
            const response = await announcementRepository.recipientCount(item.audience, companyId);
            this.confirmCount = response.data.count;
            this.confirmAudienceLabel = this.audienceSummary(item.audience, companyId);
            this.confirmScheduledFor = (item.activeFrom && new Date(item.activeFrom).getTime() > Date.now())
                ? this.formatDate(item.activeFrom) : null;
            this.confirmTargetId = item.id;
            this.confirmVisible = true;
        } catch (e) {
            this.$notifyErrorSimplified('NOTIFICATIONS.COUNT_ERROR');
        } finally {
            this.isCounting = false;
        }
    }

    private closeConfirmation(): void {
        this.confirmVisible = false;
        this.confirmTargetId = null;
    }

    private async confirmSend(): Promise<void> {
        try {
            this.isSending = true;

            let id = this.confirmTargetId;
            if (!id) {
                const created = await announcementRepository.create(this.buildPayload());
                id = created.data.id;
            }
            const published = await announcementRepository.publish(id!);

            this.$notifySuccessSimplified(published.data.status === 'SCHEDULED'
                ? 'NOTIFICATIONS.SCHEDULE_SUCCESS' : 'NOTIFICATIONS.SENDING_STARTED');
            if (!this.confirmTargetId) {
                this.resetComposer();
            }
            this.closeConfirmation();
            await this.loadHistory();
        } catch (e) {
            this.$notifyErrorSimplified('NOTIFICATIONS.SEND_ERROR');
        } finally {
            this.isSending = false;
        }
    }

    private async saveDraft(): Promise<void> {
        this.$v.$touch();
        if (this.$v.$invalid) return;

        try {
            this.isSavingDraft = true;
            await announcementRepository.create(this.buildPayload());
            this.$notifySuccessSimplified('NOTIFICATIONS.DRAFT_SUCCESS');
            this.resetComposer();
            await this.loadHistory();
        } catch (e) {
            this.$notifyErrorSimplified('NOTIFICATIONS.SEND_ERROR');
        } finally {
            this.isSavingDraft = false;
        }
    }

    private async archiveRow(item: any): Promise<void> {
        try {
            await announcementRepository.archive(item.id);
            this.$notifySuccessSimplified('NOTIFICATIONS.ARCHIVE_SUCCESS');
            await this.loadHistory();
        } catch (e) {
            this.$notifyErrorSimplified('NOTIFICATIONS.SEND_ERROR');
        }
    }

    private buildPayload(): any {
        return {
            title: this.title,
            body: this.body,
            audience: this.audience!,
            company: this.isCompanyScoped ? this.company! : undefined,
            activeFrom: this.activeFrom ? new Date(this.activeFrom).toISOString() : undefined,
            activeUntil: this.activeUntil ? new Date(this.activeUntil).toISOString() : undefined
        };
    }

    private resetComposer(): void {
        this.title = '';
        this.body = '';
        this.audience = null;
        this.company = null;
        this.activeFrom = '';
        this.activeUntil = '';
        this.$v.$reset();
    }

    private audienceLabel(audience: string): string {
        const key = audience === Audience.USER ? 'USER' : audience;
        return this.$t(`NOTIFICATIONS.AUDIENCES.${key}`).toString();
    }

    private audienceSummary(audience: Audience | string, companyId?: string | null): string {
        let label = this.audienceLabel(String(audience));
        if (String(audience) === Audience.COMPANY_DRIVERS && companyId) {
            label += ` — ${this.companyName(companyId)}`;
        }
        return label;
    }

    /** The list endpoint populates company as an object; older rows or the
     *  composer path may only hold the id, so fall back to the picker list. */
    private companyName(company: any): string {
        if (company && company.name) {
            return company.name;
        }
        const id = company && (company.id || company._id) ? (company.id || company._id) : company;
        const match = this.companies.find((c: any) => c.id === id || c._id === id);
        return match ? match.name : String(id || '');
    }

    private statusColor(status: string): string {
        switch (status) {
            case 'PUBLISHED': return 'success';
            case 'SCHEDULED': return 'info';
            case 'ARCHIVED': return 'grey';
            default: return 'warning';
        }
    }

    private formatDate(value: string | Date): string {
        return value ? new Date(value).toLocaleString() : '—';
    }
}
